import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { 
  Check, 
  X, 
  HelpCircle, 
  ThumbsUp, 
  ThumbsDown,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Users
} from 'lucide-react';
import { getFeaturesForMetro } from '../data/features';

const VOTE_THRESHOLD = 0.6; // 60% required for community verification
const MIN_VOTES = 10; // Minimum votes before auto-enabling

function FeatureVoting({ barId, barAttributes = {}, metroId }) {
  const FEATURES = getFeaturesForMetro(metroId);
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [votes, setVotes] = useState({}); // { featureName: { yes, no, total, userVote } }
  const [loading, setLoading] = useState(true);
  const [votingFeature, setVotingFeature] = useState(null);

  useEffect(() => {
    fetchVotes();
  }, [barId, user]);

  const fetchVotes = async () => {
    setLoading(true);
    try {
      // Fetch all votes for this bar
      const { data: allVotes, error } = await supabase
        .from('feature_votes')
        .select('feature_name, vote, user_id')
        .eq('bar_id', barId);

      if (error) throw error;

      // Process votes
      const voteStats = {};
      FEATURES.forEach(f => {
        const featureVotes = (allVotes || []).filter(v => v.feature_name === f.key);
        const yesVotes = featureVotes.filter(v => v.vote === true).length;
        const noVotes = featureVotes.filter(v => v.vote === false).length;
        const userVote = user 
          ? featureVotes.find(v => v.user_id === user.id)?.vote 
          : null;

        voteStats[f.key] = {
          yes: yesVotes,
          no: noVotes,
          total: featureVotes.length,
          userVote: userVote,
          percentage: featureVotes.length > 0 
            ? Math.round((yesVotes / featureVotes.length) * 100) 
            : null,
          isCommunityVerified: featureVotes.length >= MIN_VOTES && 
            (yesVotes / featureVotes.length) >= VOTE_THRESHOLD
        };
      });

      setVotes(voteStats);
    } catch (error) {
      console.error('Error fetching votes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (featureKey, vote) => {
    if (!user) {
      alert('Please sign in to vote on features');
      return;
    }

    setVotingFeature(featureKey);
    try {
      // Upsert vote (insert or update)
      const { error } = await supabase
        .from('feature_votes')
        .upsert({
          bar_id: barId,
          user_id: user.id,
          feature_name: featureKey,
          vote: vote,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'bar_id,user_id,feature_name'
        });

      if (error) throw error;

      // Refresh votes
      await fetchVotes();
    } catch (error) {
      console.error('Error voting:', error);
      alert('Failed to submit vote');
    } finally {
      setVotingFeature(null);
    }
  };

  // Get features that need more info (not yet verified by community)
  const featuresNeedingVotes = FEATURES.filter(f => {
    const stat = votes[f.key];
    return !stat?.isCommunityVerified && !barAttributes[f.key];
  });

  const verifiedFeatures = FEATURES.filter(f => {
    const stat = votes[f.key];
    return stat?.isCommunityVerified || barAttributes[f.key];
  });

  if (loading) {
    return (
      <div className="bg-dive-dark border border-dive-gray rounded-xl p-4">
        <div className="flex items-center gap-2 text-gray-400">
          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm">Loading feature info...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-dive-dark border border-dive-gray rounded-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-dive-darker transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-dive-blue/20 rounded-lg flex items-center justify-center">
            <Users className="w-5 h-5 text-dive-blue" />
          </div>
          <div className="text-left">
            <h3 className="text-white font-medium">Community Features</h3>
            <p className="text-gray-400 text-sm">
              Help verify what this bar has
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-dive-gray">
          {/* Verified Features */}
          {verifiedFeatures.length > 0 && (
            <div className="p-4 border-b border-dive-gray">
              <h4 className="text-gray-400 text-xs font-medium mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-dive-green" />
                CONFIRMED FEATURES
              </h4>
              <div className="flex flex-wrap gap-2">
                {verifiedFeatures.map(f => {
                  const stat = votes[f.key];
                  return (
                    <div
                      key={f.key}
                      className="px-3 py-1.5 bg-dive-green/20 border border-dive-green/50 rounded-full text-dive-green text-sm flex items-center gap-1"
                    >
                      <Check className="w-3 h-3" />
                      {f.label}
                      {stat?.total > 0 && (
                        <span className="text-xs text-gray-400 ml-1">
                          ({stat.yes}/{stat.total})
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Features Needing Votes */}
          <div className="p-4">
            <h4 className="text-gray-400 text-xs font-medium mb-3 flex items-center gap-2">
              <HelpCircle className="w-4 h-4" />
              HELP US VERIFY
            </h4>
            
            {!user && (
              <p className="text-gray-500 text-sm mb-4">
                Sign in to vote on features
              </p>
            )}

            <div className="space-y-3">
              {FEATURES.map(feature => {
                const stat = votes[feature.key];
                const isVoting = votingFeature === feature.key;
                
                return (
                  <div
                    key={feature.key}
                    className="bg-dive-darker rounded-lg p-3"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <p className="text-white text-sm">{feature.question}</p>
                        {stat?.total > 0 && (
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex-1 h-1.5 bg-dive-gray rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-dive-green rounded-full transition-all"
                                style={{ width: `${stat.percentage}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-400">
                              {stat.yes}/{stat.total} say yes
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {isVoting ? (
                          <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <button
                              onClick={() => handleVote(feature.key, true)}
                              disabled={!user}
                              className={`p-2 rounded-lg transition-colors ${
                                stat?.userVote === true
                                  ? 'bg-dive-green text-white'
                                  : 'bg-dive-gray text-gray-400 hover:bg-dive-green/30 hover:text-dive-green'
                              } ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title="Yes, this bar has this feature"
                            >
                              <ThumbsUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleVote(feature.key, false)}
                              disabled={!user}
                              className={`p-2 rounded-lg transition-colors ${
                                stat?.userVote === false
                                  ? 'bg-red-500 text-white'
                                  : 'bg-dive-gray text-gray-400 hover:bg-red-500/30 hover:text-red-400'
                              } ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title="No, this bar doesn't have this feature"
                            >
                              <ThumbsDown className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Info Text */}
            <p className="text-gray-500 text-xs mt-4">
              Features are marked as "verified" when at least 10 users vote and 60%+ agree.
              Your votes help the community!
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default FeatureVoting;

